import { Injectable } from '@angular/core';
import {
  RawParams,
  RejectType,
  StateParams,
  StateService,
  Transition,
  TransitionService,
} from '@uirouter/core';

import { ExceptionLoggingService } from 'src/app/components/logging/exception-logging.service';
import { RvshareAppDetectionService } from 'src/app/screen-sharing/services/rvshare-app-detection.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { LocationHelperService } from 'src/app/shared/services/location-helper.service';

const RELOAD_ON_COMPANY_CHANGE_STATES = new Set([
  'apps.schedules.list',
  'apps.editor.list',
  'apps.displays.list',
  'apps.displays.alerts',
  'apps.storage.home',
  'apps.settings.company-details',
  'apps.settings.company-licenses',
  'apps.settings.sso-settings',
  'apps.settings.integrations.list',
  'apps.settings.integrations.open',
  'apps.settings.user-list',
  'apps.company.add',
  'apps.screen-sharing.moderator-join',
  'apps.home',
]);

// Lists whose URL holds a folder of the selected company, and the Screen
// Sharing display picker. A company change reloads them at the root, because
// that folder belongs to the previous company.
const RESET_FOLDER_ON_COMPANY_CHANGE_STATES = new Set([
  'apps.schedules.list',
  'apps.editor.list',
  'apps.displays.list',
  'apps.screen-sharing.moderator-join',
]);

@Injectable({
  providedIn: 'root',
})
export class AppRouterBootstrapService {
  private initialized = false;

  constructor(
    private transitionService: TransitionService,
    private stateService: StateService,
    private exceptionLoggingService: ExceptionLoggingService,
    private rvShareAppDetection: RvshareAppDetectionService,
    private broadcasterService: BroadcasterService,
    private locationHelperService: LocationHelperService,
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.registerTransitionErrorHandler();
    this.registerRvShareGuard();
    this.registerCompanyChangeReload();
  }

  private registerTransitionErrorHandler(): void {
    this.transitionService.onError({}, (transition) => {
      this.handleTransitionError(transition);
    });
  }

  handleTransitionError(transition: Transition): void {
    const error = transition.error();

    // angular-ui-router/stateEvents.js:37 - Ignored (and Invalid) transitions are not handled
    // angular-ui-router/stateEvents.js:200 - Superseded and Aborted transitions are not thrown
    if (
      error &&
      (error.type === RejectType.SUPERSEDED ||
        error.type === RejectType.ABORTED ||
        error.type === RejectType.IGNORED)
    ) {
      return;
    }

    if (error && error.detail === 'unauthenticated') {
      return;
    }

    if (
      error &&
      error.detail &&
      (error.detail as { status?: number }).status === 404
    ) {
      if (!transition.from().name) {
        void transition.router.stateService.go(
          'apps.home',
          transition.params(),
          {
            location: 'replace',
          },
        );
      }
    }

    this.exceptionLoggingService.handle(error, 'UI Router Error.', true);
  }

  private registerRvShareGuard(): void {
    this.transitionService.onBefore(
      {
        to: (state) => {
          const isEnabledForApp =
            state.data &&
            (state.data as { rvshareAppEnabled?: boolean })
              .rvshareAppEnabled !== undefined
              ? (state.data as { rvshareAppEnabled?: boolean })
                  .rvshareAppEnabled
              : false;
          return !isEnabledForApp;
        },
      },
      (transition: Transition) => {
        if (!this.rvShareAppDetection.isScreenShareApp()) {
          return true;
        }
        // Signing in with no page to return to, as after signing out, goes
        // home. The RV Share app has no home page, and blocking it left the
        // page blank, so the moderator page stands in for it.
        if (transition.to().name === 'apps.home') {
          return this.stateService.target(
            'apps.screen-sharing.moderator-join',
            { ...transition.params(), rvshare: true },
          );
        }
        return false;
      },
    );
  }

  private registerCompanyChangeReload(): void {
    // SelectedCompanyUrlHandlerService registers first (via
    // UserstateBootstrapService) and may call `go('.')` after mutating the
    // shared `stateService.params` with the new `cid`. This reload listener
    // intentionally supersedes that transition; the mutated params carry the
    // new `cid` into the winning `go(..., { reload: true })`. The superseded
    // rejection is swallowed by `updateUrl()`'s `.catch` and ignored by
    // `handleTransitionError` for `RejectType.SUPERSEDED`.
    this.broadcasterService.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.reloadForSelectedCompanyChange();
      },
    );
  }

  reloadForSelectedCompanyChange(): void {
    const current = this.stateService.current as {
      name?: string;
      forceAuth?: boolean;
      params?: { forceAuth?: boolean };
    };
    const currentName = current?.name || '';
    const params = this.stateService.params;

    if (RELOAD_ON_COMPANY_CHANGE_STATES.has(currentName)) {
      void this.stateService.go(
        currentName,
        RESET_FOLDER_ON_COMPANY_CHANGE_STATES.has(currentName)
          ? this.atListRoot(params)
          : params,
        { reload: true },
      );
      return;
    }

    if (
      (currentName.indexOf('apps.purchase') !== -1 ||
        currentName.indexOf('apps.billing') !== -1 ||
        currentName.indexOf('apps.settings.billing') !== -1) &&
      current.forceAuth !== false &&
      (!current.params || current.params.forceAuth !== false)
    ) {
      void this.stateService.go('apps.settings.billing.home', params, {
        reload: true,
      });
      return;
    }

    if (currentName === 'apps.editor.templates.edit') {
      if (params['cid'] !== this.locationHelperService.getSearchParam('cid')) {
        void this.stateService.go('apps.editor.list', this.atListRoot(params));
      }
    }
  }

  /**
   * The current params pointing at the root of a list. `folderId` is set to
   * null rather than left out: UI-Router inherits an omitted param from the
   * current state, which would keep the previous company's folder.
   */
  private atListRoot(params: StateParams): RawParams {
    return { ...params, folderId: null };
  }
}
